import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Zap, Building2, Star, ArrowRight } from 'lucide-react';

const FEATURES = [
  'Sınırsız fuar yönetimi',
  'İnteraktif kroki editörü',
  'Firma ve stant yönetimi',
  'Sözleşme oluşturma & PDF',
  'E-posta entegrasyonu',
  'Takım üyesi ekleme',
  'Raporlar ve analizler',
  '7/24 destek',
];

export default function Pricing() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState('yearly');
  const [loading, setLoading] = useState(false); // false | 'trial' | 'buy'

  useEffect(() => {
    if (user?.subscription_status === 'active') {
      navigate('/organizer');
    }
  }, [user, navigate]);

  // Free trial → register (no payment yet) or existing user direct checkout
  const handleSubscribe = async () => {
    if (!user) {
      navigate('/register?plan=' + selectedPlan);
      return;
    }
    setLoading('trial');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/billing/create-checkout-session`,
        { plan_id: selectedPlan, origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ödeme başlatılamadı');
      setLoading(false);
    }
  };

  // Direct buy → register with mode=direct (immediate Stripe after signup) or existing user checkout
  const handleDirectBuy = async () => {
    if (!user) {
      navigate(`/register?plan=${selectedPlan}&mode=direct`);
      return;
    }
    setLoading('buy');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/billing/create-checkout-session`,
        { plan_id: selectedPlan, origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ödeme başlatılamadı');
      setLoading(false);
    }
  };

  const monthlyPrice = 39;
  const yearlyPrice = 375;
  const yearlyMonthly = (yearlyPrice / 12).toFixed(0);
  const discount = Math.round((1 - yearlyPrice / (monthlyPrice * 12)) * 100);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="border-b border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 group">
            <div className="w-9 h-9 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-gray-900 text-lg group-hover:text-teal-700 transition-colors">
              Expogrid.io
            </span>
          </button>
          {!user ? (
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/login')} className="text-sm text-gray-500 hover:text-gray-700 font-medium">
                Giriş Yap
              </button>
              <button
                onClick={() => navigate('/register')}
                className="text-sm bg-gray-900 hover:bg-gray-800 text-white font-semibold px-4 py-1.5 rounded-lg transition-colors"
              >
                Ücretsiz Dene
              </button>
            </div>
          ) : (
            <button onClick={() => navigate('/organizer')} className="text-sm text-teal-600 hover:text-teal-800 font-medium">
              Dashboard'a Git →
            </button>
          )}
        </div>
      </header>

      {/* Two-column hero */}
      <div className="flex-1 flex items-center">
        <div className="max-w-6xl mx-auto px-6 py-8 w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

          {/* LEFT: Copy */}
          <div className="space-y-6">
            {/* Badge */}
            <div className="inline-flex items-center gap-1.5 bg-teal-50 border border-teal-200 text-teal-700 text-xs font-semibold px-3 py-1.5 rounded-full">
              <Zap className="w-3 h-3" />
              14 Gün Ücretsiz Deneme — Kredi kartı gerekmez
            </div>

            {/* Heading */}
            <div>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 leading-tight">
                Fuar Yönetimini
              </h1>
              <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-emerald-600">
                Profesyonelleştirin
              </h1>
            </div>

            {/* Sub */}
            <p className="text-gray-500 text-lg leading-relaxed max-w-md">
              Fuarlarınızı, firmalarınızı ve sözleşmelerinizi tek platformdan yönetin.
              14 gün boyunca tüm özellikleri ücretsiz deneyin.
            </p>

            {/* Features list (compact) */}
            <div className="grid grid-cols-2 gap-y-2 gap-x-4">
              {FEATURES.map((f, i) => (
                <div key={i} className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0" />
                  <span className="text-sm text-gray-600">{f}</span>
                </div>
              ))}
            </div>

            {/* Trust */}
            <div className="flex items-center gap-6 pt-2">
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">500+</p>
                <p className="text-xs text-gray-400">Aktif Fuar</p>
              </div>
              <div className="h-10 w-px bg-gray-200" />
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">%99.9</p>
                <p className="text-xs text-gray-400">Uptime</p>
              </div>
              <div className="h-10 w-px bg-gray-200" />
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">14 Gün</p>
                <p className="text-xs text-gray-400">Ücretsiz</p>
              </div>
            </div>
          </div>

          {/* RIGHT: Pricing Card */}
          <div className="flex flex-col" data-testid="pricing-card">
            {/* Plan Toggle */}
            <div className="flex bg-gray-100 rounded-xl p-1 mb-5 max-w-xs">
              <button
                onClick={() => setSelectedPlan('monthly')}
                className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all ${
                  selectedPlan === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="plan-monthly-btn"
              >
                Aylık
              </button>
              <button
                onClick={() => setSelectedPlan('yearly')}
                className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all relative ${
                  selectedPlan === 'yearly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="plan-yearly-btn"
              >
                Yıllık
                <span className="absolute -top-2.5 -right-1 bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                  -{discount}%
                </span>
              </button>
            </div>

            {/* Card */}
            <div className="bg-white rounded-2xl border-2 border-teal-500 shadow-2xl overflow-hidden">
              {/* Card Header */}
              <div className="bg-gradient-to-br from-teal-500 to-emerald-600 px-7 py-6 text-white">
                <div className="flex items-center gap-1.5 mb-3">
                  <Star className="w-4 h-4 fill-white" />
                  <span className="text-sm font-semibold uppercase tracking-wide">
                    {selectedPlan === 'yearly' ? 'Yıllık Plan' : 'Aylık Plan'}
                  </span>
                </div>

                {selectedPlan === 'yearly' ? (
                  <div className="flex items-end gap-2">
                    <div>
                      <div className="flex items-end gap-1">
                        <span className="text-5xl font-extrabold">${yearlyMonthly}</span>
                        <span className="text-teal-100 mb-1.5 text-base">/ay</span>
                      </div>
                      <p className="text-teal-100 text-sm">Yıllık ${yearlyPrice} fatura edilir</p>
                    </div>
                    <div className="ml-auto">
                      <div className="bg-white/20 text-white text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap">
                        ${monthlyPrice * 12 - yearlyPrice} Tasarruf
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-end gap-1">
                      <span className="text-5xl font-extrabold">${monthlyPrice}</span>
                      <span className="text-teal-100 mb-1.5 text-base">/ay</span>
                    </div>
                    <p className="text-teal-100 text-sm">Aylık fatura edilir</p>
                  </div>
                )}
              </div>

              {/* CTA - Two buttons */}
              <div className="px-7 pt-5 pb-4 space-y-2">
                {/* Free Trial */}
                <button
                  onClick={handleSubscribe}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 border-2 border-gray-200 hover:border-gray-300 bg-gray-50 hover:bg-gray-100 text-gray-700 font-semibold rounded-xl text-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="free-trial-btn"
                >
                  {loading === 'trial' ? 'Yönlendiriliyor...' : (
                    <>14 Gün Ücretsiz Başla</>
                  )}
                </button>

                {/* Direct Buy */}
                <button
                  onClick={handleDirectBuy}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-gray-900 hover:bg-gray-800 text-white font-bold rounded-xl text-sm transition-all shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="direct-buy-btn"
                >
                  {loading === 'buy' ? 'Yönlendiriliyor...' : (
                    <>
                      Hemen Satın Al
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>

                <p className="text-center text-xs text-gray-400">
                  Güvenli ödeme · İstediğiniz zaman iptal
                </p>
              </div>

              {/* Divider */}
              <div className="mx-7 border-t border-gray-100" />

              {/* Features in two columns */}
              <div className="px-7 py-4 grid grid-cols-2 gap-x-4 gap-y-2">
                {FEATURES.map((f, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" />
                    <span className="text-xs text-gray-600">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Register / Login links */}
            {!user && (
              <div className="flex items-center justify-center gap-4 mt-4 text-sm text-gray-500">
                <span>
                  Hesabınız var mı?{' '}
                  <button onClick={() => navigate('/login')} className="text-teal-600 hover:text-teal-800 font-semibold">
                    Giriş Yapın
                  </button>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
